import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  /** ms before auto-dismiss; null disables. Default 4000. */
  duration: number | null;
}

interface UIState {
  isQuickViewOpen: boolean;
  quickViewProductSlug: string | null;
  isFilterDrawerOpen: boolean;
  isMobileNavOpen: boolean;
  isNavScrolled: boolean;

  toasts: Toast[];

  openQuickView: (slug: string) => void;
  closeQuickView: () => void;

  openFilterDrawer: () => void;
  closeFilterDrawer: () => void;
  toggleFilterDrawer: () => void;

  openMobileNav: () => void;
  closeMobileNav: () => void;

  setNavScrolled: (v: boolean) => void;

  addToast: (toast: Omit<Toast, 'id' | 'duration'> & { duration?: number | null }) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

export const useUIStore = create<UIState>()((set) => ({
  isQuickViewOpen: false,
  quickViewProductSlug: null,
  isFilterDrawerOpen: false,
  isMobileNavOpen: false,
  isNavScrolled: false,

  toasts: [],

  openQuickView: (slug) => set({ isQuickViewOpen: true, quickViewProductSlug: slug }),
  closeQuickView: () => set({ isQuickViewOpen: false, quickViewProductSlug: null }),

  openFilterDrawer: () => set({ isFilterDrawerOpen: true }),
  closeFilterDrawer: () => set({ isFilterDrawerOpen: false }),
  toggleFilterDrawer: () => set((s) => ({ isFilterDrawerOpen: !s.isFilterDrawerOpen })),

  openMobileNav: () => set({ isMobileNavOpen: true }),
  closeMobileNav: () => set({ isMobileNavOpen: false }),

  setNavScrolled: (v) => set({ isNavScrolled: v }),

  addToast: ({ duration, ...rest }) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const toast: Toast = { id, duration: duration ?? 4000, ...rest };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    return id;
  },
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clearToasts: () => set({ toasts: [] }),
}));

/** One-line helper used throughout — `toast.success('Added to cart')`. */
export const toast = {
  success: (title: string, description?: string) =>
    useUIStore.getState().addToast({ title, description, variant: 'success' }),
  error: (title: string, description?: string) =>
    useUIStore.getState().addToast({ title, description, variant: 'error' }),
  info: (title: string, description?: string) =>
    useUIStore.getState().addToast({ title, description, variant: 'info' }),
  warning: (title: string, description?: string) =>
    useUIStore.getState().addToast({ title, description, variant: 'warning' }),
};
